import { NudgeAnimationOptions } from 'app/services/helpCenter';
import { motion } from 'framer-motion';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import useNamespacedStorage from 'app/hooks/storage/useNamespacedStorage';
import useIsMobile from 'app/hooks/useIsMobile';

import { bouncy } from './bouncy';

type Props = PropsWithChildren<{
  options: NudgeAnimationOptions;
}>;

const animations = new Map([
  ['bouncy', bouncy],
  // Add more animations here
]);

const defaultOptions: NudgeAnimationOptions = {
  enable_animation: false,
  animation_style: 'bouncy',
  animation_duration: 0,
  animation_repeats: 0,
  repeat_delay: 0,
  reset_interval: 0,
  enable_desktop_animation: false,
};

let nudgeTimestamp: number = Date.now();
// time in seconds since last nudge
export const getTimeSinceLastNudge = () => {
  return (Date.now() - nudgeTimestamp) / 1000;
};
const setNudgeTime = (time: number) => {
  nudgeTimestamp = time;
};

const NudgeAnimation = ({ children, options = defaultOptions }: Props) => {
  const intervalId = useRef<number>(0);
  const isMobile = useIsMobile();
  const [animationStarted, setAnimationStarted] = useState(false);
  const [animationPlayed, setAnimationPlayed] = useNamespacedStorage('ostroCTAAnimationPlayed', false);

  // Reset the animation after a certain interval
  useEffect(() => {
    if (options.reset_interval) {
      const interval = (options.reset_interval ?? 0) * 60 * 1000;

      if (!intervalId.current) {
        intervalId.current = window.setInterval(() => {
          setAnimationPlayed(false);
        }, interval);
      }
    }
  }, [options.reset_interval, setAnimationPlayed]);

  // Clear the interval when the page is closed
  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      if (intervalId.current) clearInterval(intervalId.current);
      setAnimationPlayed(false);
    });
  }, [setAnimationPlayed]);

  // If the animation has started and the component is unmounted, set played to true
  useEffect(() => {
    return () => {
      if (animationStarted) setAnimationPlayed(true);
    };
  }, [animationStarted, setAnimationPlayed]);

  const variants = useMemo(() => {
    return animations.get(options.animation_style);
  }, [options.animation_style]);

  const enabledOnDesktop = options.enable_desktop_animation;
  const enabledOnMobile = options.enable_animation;

  const enabled = isMobile ? enabledOnMobile : enabledOnDesktop;
  const skipAnimation = !enabled || animationPlayed;

  return (
    <motion.div
      custom={options}
      variants={skipAnimation ? undefined : variants}
      initial="initial"
      animate="animate"
      data-animation-playing={animationStarted && !animationPlayed}
      className="pointer-events-none"
      onAnimationStart={() => {
        setNudgeTime(Date.now());
        setAnimationStarted(true);
      }}
      onAnimationComplete={() => {
        setAnimationPlayed(true);
      }}
    >
      {children}
    </motion.div>
  );
};

export default NudgeAnimation;
